import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";
import MDButton from "components/MDButton";
import { telechargerFichier } from "utils/telechargement";

// Couleurs des statuts (liste d'attente bien visible : qui peut partir si une place se libère)
const couleurStatut = {
  "Confirmée": { bg: "#e8f5e9", fg: "#2e7d32" },
  "Validée": { bg: "#e3f2fd", fg: "#1565c0" },
  "En attente": { bg: "#f5f5f5", fg: "#616161" },
  "Liste d'attente": { bg: "#fff3e0", fg: "#e65100" },
  "Accompagnateur": { bg: "#f3e5f5", fg: "#6a1b9a" },
};
const colonnes = ["Nom", "Prénom", "Sexe", "Date de naissance", "Téléphone", "Passeport", "Chambre", "Directeur", "Statut"];

// "AAAA-MM-JJ" -> "JJ/MM/AAAA" sans passer par Date (pas de décalage de fuseau)
const dateLongue = (iso) => (iso ? iso.slice(8, 10) + "/" + iso.slice(5, 7) + "/" + iso.slice(0, 4) : "—");

function OumrahDeparts() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/oumrah/sessions").then((res) => setSessions(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sessionId) { setData(null); return; }
    setLoading(true);
    api.get("/oumrah/sessions/" + sessionId + "/departs")
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [sessionId]);

  const [exportEnCours, setExportEnCours] = useState(false);
  const exporterExcel = async () => {
    setExportEnCours(true);
    try {
      await telechargerFichier("/oumrah/sessions/" + sessionId + "/departs/excel", "departs.xlsx");
    } catch (err) {
      alert("Erreur lors de la génération de l'Excel");
    } finally {
      setExportEnCours(false);
    }
  };

  const cellule = { py: 0.6, px: 1, fontSize: "0.78rem", borderBottom: "1px solid #f0f0f0" };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <MDTypography variant="h5" mb={1}>Départs par ville et date</MDTypography>
              <MDTypography variant="button" color="text" mb={2} display="block">
                Qui part, d'où et quand. Inscriptions annulées exclues ; la liste d'attente est affichée (statut orange).
              </MDTypography>
              <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 280 }}>
                  <InputLabel id="sess-departs">Session</InputLabel>
                  <Select labelId="sess-departs" label="Session" sx={{ height: 44 }}
                    value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
                    <MenuItem value="">—</MenuItem>
                    {sessions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.libelle}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <MDButton size="small" color="success" variant="outlined" onClick={exporterExcel}
                  disabled={!data || exportEnCours}>
                  <Icon>table_view</Icon>&nbsp;{exportEnCours ? "Génération…" : "Exporter Excel"}
                </MDButton>
              </MDBox>
            </Card>
          </Grid>

          {loading && (
            <Grid item xs={12}>
              <MDTypography variant="button" color="text">Chargement…</MDTypography>
            </Grid>
          )}

          {data && !loading && (
            <>
              {/* Synthèse : une pastille par ville-date */}
              <Grid item xs={12}>
                <MDBox display="flex" flexWrap="wrap" gap={1}>
                  {data.groupes.map((g) => (
                    <Chip key={g.ville_depart_id || "nr"}
                      icon={<Icon fontSize="small">flight_takeoff</Icon>}
                      label={g.libelle + " : " + g.stats.total}
                      sx={{ fontWeight: "bold", bgcolor: g.date_depart ? "#e3f2fd" : "#fff3e0", color: g.date_depart ? "#1565c0" : "#e65100" }} />
                  ))}
                  <Chip label={"Total : " + data.total_personnes} sx={{ fontWeight: "bold" }} />
                </MDBox>
              </Grid>

              {data.groupes.map((g) => (
                <Grid item xs={12} key={g.ville_depart_id || "nr"}>
                  <Card sx={{ p: 2.5, borderTop: "4px solid " + (g.date_depart ? "#1A73E8" : "#e67e22") }}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={1}>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Icon sx={{ color: g.date_depart ? "#1A73E8" : "#e67e22" }}>flight_takeoff</Icon>
                        <MDTypography variant="h6">
                          {g.ville || "Ville de départ non renseignée"}
                          {g.ville && (g.date_depart
                            ? " — départ le " + dateLongue(g.date_depart)
                            : " — date non renseignée")}
                        </MDTypography>
                      </MDBox>
                      <MDTypography variant="caption" color="text">
                        {g.stats.total} personne(s) · {g.stats.hommes} H / {g.stats.femmes} F
                        {g.places_total != null && " · " + g.stats.hors_liste_attente + " / " + g.places_total + " places"}
                        {g.stats.liste_attente > 0 && " · " + g.stats.liste_attente + " en liste d'attente"}
                      </MDTypography>
                    </MDBox>
                    {g.personnes.length === 0 ? (
                      <MDTypography variant="caption" color="text">Personne pour ce départ.</MDTypography>
                    ) : (
                      <MDBox sx={{ overflowX: "auto" }}>
                        <Table size="small" sx={{ minWidth: 900 }}>
                          <TableBody>
                            <TableRow>
                              {colonnes.map((c) => (
                                <TableCell key={c} sx={{ ...cellule, fontWeight: "bold", color: "#7b809a", textTransform: "uppercase", fontSize: "0.68rem" }}>{c}</TableCell>
                              ))}
                            </TableRow>
                            {g.personnes.map((p) => {
                              const coul = couleurStatut[p.statut_affiche] || couleurStatut["En attente"];
                              return (
                                <TableRow key={p.id} sx={{ bgcolor: p.liste_attente ? "#fffaf3" : "inherit" }}>
                                  <TableCell sx={{ ...cellule, fontWeight: "bold" }}>{(p.nom || "").toUpperCase()}</TableCell>
                                  <TableCell sx={cellule}>{p.prenom}</TableCell>
                                  <TableCell sx={cellule}>{p.sexe || "—"}</TableCell>
                                  <TableCell sx={cellule}>{dateLongue(p.date_naissance)}</TableCell>
                                  <TableCell sx={cellule}>{p.telephone || "—"}</TableCell>
                                  <TableCell sx={cellule}>{p.num_passeport || "—"}</TableCell>
                                  <TableCell sx={cellule}>{p.tarif_libelle || "—"}</TableCell>
                                  <TableCell sx={cellule}>{p.directeur_nom}{p.directeur_ville ? " (" + p.directeur_ville + ")" : ""}</TableCell>
                                  <TableCell sx={cellule}>
                                    <Chip size="small" label={p.statut_affiche} sx={{ height: 20, fontSize: "0.68rem", fontWeight: "bold", bgcolor: coul.bg, color: coul.fg }} />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </MDBox>
                    )}
                  </Card>
                </Grid>
              ))}
            </>
          )}
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default OumrahDeparts;
